/**
 * Angular Date Range Component
 *
 * Migrated version of the AngularJS acme-date-range component.
 * Original: apps/b2b-ui/common/components/date-range/date-range.component.ts
 *
 * The original relied on the UI Bootstrap `uib-datepicker-popup` directive,
 * which has no Angular 18 equivalent and no datepicker library is installed in
 * this app. It has been replaced with the native `<input type="date">`, which
 * integrates with Angular forms out of the box (no new dependency). As a result
 * the Bootstrap-only datepicker options (startingDay / formatDayTitle /
 * showWeeks) and the `UserPreferencesService` gating are no longer needed —
 * `minDate` / `maxDate` map to the native `min` / `max` attributes instead.
 *
 * It implements ControlValueAccessor so it works with template-driven
 * (`[(ngModel)]`) and reactive (`formControl`) Angular forms — the idiomatic
 * replacement for the AngularJS `require: { ngModelCtrl: 'ngModel' }` pattern.
 * It also exposes a `value` two-way binding for use outside of forms and for the
 * AngularJS downgrade.
 *
 * Usage in Angular templates:
 *   <app-date-range [(ngModel)]="query" mode="custom" initialRangeType="current_year"></app-date-range>
 *   <app-date-range [(value)]="query" mode="range"></app-date-range>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-date-range mode="'custom'"
 *                  initial-range-type="'current_year'"
 *                  value="ctrl.query"
 *                  on-value-change="ctrl.query = $event"></ng-date-range>
 */
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CalendarService } from '../../../core/calendar/calendar.service';
import type { CalendarRangeOption, CalendarRangeType } from '../../../core/calendar/calendar.service';
import type { Maybe, Timestamp } from '@b2b/types/core';

export type DateMode = 'custom' | 'range';

export interface DateRangeValue {
  startDate: Timestamp;
  endDate: Timestamp;
}

let uniqueId = 0;

@Component({
  selector: 'app-date-range',
  standalone: true,
  imports: [NgIf, NgFor, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeComponent),
      multi: true,
    },
  ],
  template: require('./date-range.component.html'),
  styles: [require('./date-range.component.less')],
})
export class DateRangeComponent implements OnInit, OnChanges, ControlValueAccessor {
  private calendarService = inject(CalendarService);

  /** Unique id linking each <input>/<label> for this instance */
  readonly id = ++uniqueId;

  @Input() required = false;
  @Input() disabled = false;
  @Input() mode: DateMode = 'custom';
  @Input() minDate: Maybe<Date | Timestamp>;
  @Input() maxDate: Maybe<Date | Timestamp>;
  @Input() initialRangeType: CalendarRangeType = 'today';

  /** Value — supports `[(value)]` two-way binding outside of forms / downgrade */
  @Input() value: DateRangeValue = { startDate: '', endDate: '' };
  @Output() valueChange = new EventEmitter<DateRangeValue>();

  /** Native `<input type="date">` bound values, in `yyyy-MM-dd` form */
  startDt = '';
  endDt = '';

  /** Native `min`/`max` attribute values, in `yyyy-MM-dd` form */
  minAttr: string | null = null;
  maxAttr: string | null = null;

  ranges: CalendarRangeOption[] = this.calendarService.ranges;
  selectedRange: CalendarRangeOption | null = null;

  private onChange: (value: DateRangeValue) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    this.value = this.coerceValue(this.value);
    this.initRanges();
    this.setDateMode(this.mode);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minDate']) {
      this.minAttr = this.toInputDate(this.minDate);
    }

    if (changes['maxDate']) {
      this.maxAttr = this.toInputDate(this.maxDate);
    }

    if (changes['value']) {
      this.value = this.coerceValue(this.value);
      this.syncInputsFromValue();
    }
  }

  isDateMode(mode: DateMode): boolean {
    return this.mode === mode;
  }

  setDateMode(mode: DateMode): void {
    this.mode = mode;

    if (this.mode === 'range' && this.selectedRange) {
      const range = this.selectedRange.logic(new Date());
      this.value.startDate = this.coerceTimestamp(range.startDate);
      this.value.endDate = this.coerceTimestamp(range.endDate);
    } else if (this.mode === 'custom') {
      this.ensureDefaultDates();
      this.syncInputsFromValue();
    }

    this.emitValue();
  }

  onStartDateChange(inputValue: string): void {
    this.startDt = inputValue;
    const date = this.parseInputDate(inputValue);
    this.value.startDate = date ? this.startOfDay(date) : ('' as Timestamp);
    this.emitValue();
  }

  onEndDateChange(inputValue: string): void {
    this.endDt = inputValue;
    const date = this.parseInputDate(inputValue);
    this.value.endDate = date ? this.endOfDay(date) : ('' as Timestamp);
    this.emitValue();
  }

  onSelectedRangeChange(range: CalendarRangeOption): void {
    this.selectedRange = range;
    this.value.startDate = range.startDate;
    this.value.endDate = range.endDate;
    this.emitValue();
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────
  writeValue(value: Maybe<DateRangeValue>): void {
    this.value = this.coerceValue(value);
    // Default any missing date to today in custom mode, and notify the form
    // model so it reflects the defaulted value (only when something changed,
    // to avoid a write/change loop).
    if (this.mode === 'custom' && this.ensureDefaultDates()) {
      this.emitValue();
    }
    this.syncInputsFromValue();
  }

  registerOnChange(fn: (value: DateRangeValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ── internals ─────────────────────────────────────────────────────────────
  private initRanges(): void {
    for (const range of this.ranges) {
      range.logic(new Date());
    }

    this.selectedRange = this.ranges.find((range) => range.type === this.initialRangeType) ?? this.ranges[0];
  }

  private emitValue(): void {
    const next = { ...this.value };
    this.value = next;
    this.valueChange.emit(next);
    this.onChange(next);
  }

  private syncInputsFromValue(): void {
    this.startDt = this.toInputDate(this.value.startDate);
    this.endDt = this.toInputDate(this.value.endDate);
  }

  /**
   * Guarantee `this.value` is a well-formed object with `startDate`/`endDate`.
   * Tolerates a missing, null or non-object binding (e.g. an unevaluated
   * string) and preserves any extra fields the consumer carries (`filterType`).
   */
  private coerceValue(value: unknown): DateRangeValue {
    if (value == null || typeof value !== 'object') {
      return { startDate: '', endDate: '' };
    }

    const partial = value as Partial<DateRangeValue>;
    return {
      ...(value as Record<string, unknown>),
      startDate: partial.startDate ?? ('' as Timestamp),
      endDate: partial.endDate ?? ('' as Timestamp),
    } as DateRangeValue;
  }

  /**
   * Default any missing date to today (start-of-day for `startDate`,
   * end-of-day for `endDate`). Returns whether anything changed.
   */
  private ensureDefaultDates(): boolean {
    const now = new Date();
    let changed = false;

    if (!this.value.startDate) {
      this.value.startDate = this.startOfDay(now);
      changed = true;
    }

    if (!this.value.endDate) {
      this.value.endDate = this.endOfDay(now);
      changed = true;
    }

    return changed;
  }

  private coerceTimestamp(value: Maybe<Date | Timestamp>): Timestamp {
    if (value == null) {
      return '' as Timestamp;
    }

    return (value instanceof Date ? value.toISOString() : value) as Timestamp;
  }

  /** Format an ISO timestamp / Date into the `yyyy-MM-dd` the native input uses. */
  private toInputDate(value: Maybe<Date | Timestamp>): string {
    if (value == null || value === '') {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** ISO timestamp at the start of the given date's day (00:00:00.000). */
  private startOfDay(date: Date): Timestamp {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString() as Timestamp;
  }

  /** ISO timestamp at the end of the given date's day (23:59:59.999). */
  private endOfDay(date: Date): Timestamp {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString() as Timestamp;
  }

  /** Build a local Date from a `yyyy-MM-dd` string (avoids UTC parsing drift). */
  private parseInputDate(inputValue: string): Date | null {
    if (!inputValue) {
      return null;
    }
    const [year, month, day] = inputValue.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }
}
